import React from "react";
import { createPortal } from "react-dom";
import CircleQuestionOutlineIcon from "@src/lib/icons/18px/circle-question-outline";
import { usePortalTarget } from "@src/lib/hooks/usePortalTarget";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/lib/ui/Tooltip";

// The count chip beside a list page's title.
export const PAGE_HEADER_COUNT_CLASS =
  "rounded-md bg-bg-secondary px-1.5 py-0.5 text-center text-2xs-v4 font-medium-v4 text-text-tertiary tabular-nums";

// The info (?) button beside a page title; its tooltip carries the
// description.
export const PAGE_HEADER_INFO_BUTTON_CLASS =
  "inline-flex items-center rounded-sm text-icon-tertiary transition-colors outline-none hover:text-icon-secondary focus-visible:ring-1 focus-visible:ring-brand-border";

type Props = {
  count?: number;
  /**
   * Unfiltered total. Pass alongside a filtered `count` and the chip reads
   * "X of Y" while a filter narrows the list, matching the count row on
   * paginated tables. Omit when the page has no filters.
   */
  totalCount?: number;
  countLoading?: boolean;
  description?: React.ReactNode;
  learnMoreLink?: string;
  subtitle?: React.ReactNode;
  titleExtra?: React.ReactNode;
  actions?: React.ReactNode;
};

/**
 * Portals page-header chrome into the page content header zone:
 *  - optional count + info tooltip rendered next to the page title
 *  - optional subtitle rendered below the page title
 *  - optional action buttons rendered on the right side
 *
 * The title itself is rendered by PageContentHeaderZone via the route map.
 * Callers should only pass the count, description, and action buttons.
 */
const TopNavPageHeader: React.FC<Props> = ({
  count,
  totalCount,
  countLoading,
  description,
  learnMoreLink,
  subtitle,
  titleExtra,
  actions,
}) => {
  const titlePortal = usePortalTarget("top-nav-page-title-extra");
  const subtitlePortal = usePortalTarget("top-nav-page-subtitle");
  const actionsPortal = usePortalTarget("top-nav-actions");

  const hasTitleContent =
    count !== undefined || description || titleExtra !== undefined;

  return (
    <>
      {titlePortal &&
        hasTitleContent &&
        createPortal(
          <>
            {(count !== undefined || countLoading) && (
              <span className={PAGE_HEADER_COUNT_CLASS}>
                {countLoading
                  ? "–"
                  : totalCount !== undefined && totalCount !== count
                    ? `${count?.toLocaleString("en-US")} of ${totalCount.toLocaleString("en-US")}`
                    : count?.toLocaleString("en-US")}
              </span>
            )}
            {titleExtra}
            {description && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="About this page"
                      className={PAGE_HEADER_INFO_BUTTON_CLASS}
                    >
                      <CircleQuestionOutlineIcon className="!size-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-64">
                    <p className="text-2xs-v4">{description}</p>
                    {learnMoreLink && (
                      <a
                        href={learnMoreLink}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 block text-2xs-v4 text-brand-text hover:underline"
                      >
                        Learn more
                      </a>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </>,
          titlePortal,
        )}
      {subtitlePortal &&
        subtitle &&
        createPortal(
          <p className="text-s-v4 text-text-secondary">{subtitle}</p>,
          subtitlePortal,
        )}
      {actionsPortal && actions && createPortal(actions, actionsPortal)}
    </>
  );
};

export default TopNavPageHeader;
